import { type HTMLAttributes, forwardRef } from 'react';

export interface CardProps extends HTMLAttributes<HTMLDivElement> {
  variant?: 'default' | 'highlight' | 'interactive' | 'danger';
}

export const Card = forwardRef<HTMLDivElement, CardProps>(
  ({ children, variant = 'default', className = '', ...props }, ref) => {
    const variantStyles = {
      default: 'bg-navy-light/40 border-white/10 hover:border-white/20',
      highlight: 'bg-navy-light/60 border-brand-orange/40 shadow-lg shadow-brand-orange/5',
      interactive:
        'bg-navy-light/40 border-white/10 hover:border-brand-orange/50 hover:bg-navy-light/70 cursor-pointer transition-all',
      danger: 'bg-feedback-danger/10 border-feedback-danger/30 text-white',
    };

    return (
      <div
        ref={ref}
        className={`rounded-2xl border p-5 sm:p-6 backdrop-blur-sm transition-all duration-150 ${variantStyles[variant]} ${className}`}
        {...props}
      >
        {children}
      </div>
    );
  },
);
Card.displayName = 'Card';

export function CardHeader({
  children,
  className = '',
  ...props
}: HTMLAttributes<HTMLDivElement>) {
  return (
    <div className={`mb-4 flex flex-col gap-1.5 ${className}`} {...props}>
      {children}
    </div>
  );
}

export function CardTitle({
  children,
  className = '',
  ...props
}: HTMLAttributes<HTMLHeadingElement>) {
  return (
    <h3 className={`text-lg font-bold text-white sm:text-xl ${className}`} {...props}>
      {children}
    </h3>
  );
}

export function CardDescription({
  children,
  className = '',
  ...props
}: HTMLAttributes<HTMLParagraphElement>) {
  return (
    <p className={`text-sm text-brand-offWhite/80 ${className}`} {...props}>
      {children}
    </p>
  );
}

export function CardContent({
  children,
  className = '',
  ...props
}: HTMLAttributes<HTMLDivElement>) {
  return (
    <div className={`text-sm text-brand-offWhite ${className}`} {...props}>
      {children}
    </div>
  );
}

export function CardFooter({
  children,
  className = '',
  ...props
}: HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={`mt-5 flex items-center justify-between border-t border-white/10 pt-4 ${className}`}
      {...props}
    >
      {children}
    </div>
  );
}
